import { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    Paper,
    Avatar,
    CssBaseline,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Grid,
    Link,
    FormControlLabel,
    Switch,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}synctrack/tenant/post`;

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 1.5,
        backgroundColor: '#fff',
    },
};

const Register = () => {
    const [companyName, setCompanyName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [userPassword, setUserPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [userFirstName, setUserFirstName] = useState('');
    const [userLastName, setUserLastName] = useState('');
    const [userAddress, setUserAddress] = useState('');
    const [country, setCountry] = useState('');
    const [userPhone, setUserPhone] = useState('');
    const [planName, setPlanName] = useState('Trial');
    const [subscription, setSubscription] = useState('monthly');
    const [numberOfUsers, setNumberOfUsers] = useState('');
    const [cdxUser, setCdxUser] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;
    const phonePattern = /^\d{10}$/;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (userPassword !== confirmPassword) {
            setError('Passwords do not match');
            toast.error('Passwords do not match');
            return;
        }

        if (!passwordPattern.test(userPassword)) {
            setError('Password must be at least 8 characters long, with at least one uppercase letter, one lowercase letter, and one special character (!@#$%^&*).');
            toast.error('Password must be at least 8 characters long, with at least one uppercase letter, one lowercase letter, and one special character (!@#$%^&*).');
            return;
        }

        if (!phonePattern.test(userPhone)) {
            setError('Phone number must be exactly 10 digits.');
            toast.error('Phone number must be exactly 10 digits.');
            return;
        }

        if (!companyName || !userEmail || !userPassword || !userFirstName || !userLastName || !userAddress || !country || !userPhone || !numberOfUsers) {
            setError('All fields are required');
            toast.error('All fields are required');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                companyName,
                userEmail,
                userPassword,
                confirmPassword,
                userFirstName,
                userLastName,
                userAddress,
                country,
                userPhone,
                applicationId: 1,
                planName,
                subscription,
                numberOfUsers: parseInt(numberOfUsers),
                products: [],
                cdxUser: !!cdxUser,
            };

            const response = await fetch(API_BASE_URL, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const text = await response.text();
                let data;
                try {
                    data = text ? JSON.parse(text) : {};
                } catch {
                    throw new Error('Failed to parse response as JSON');
                }
                throw new Error(data.message || `HTTP error! status: ${response.status}`);
            }

            await response.json();
            toast.success('Registration successful!', { autoClose: 2000 });
            navigate('/');
        } catch (err) {
            setError(`Registration failed: ${err.message}`);
            toast.error(`Registration failed: ${err.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 2,
                px: 1,
                bgcolor: '#f4f5f7',
            }}
        >
            <CssBaseline />
            <Container component="main" maxWidth="sm" disableGutters>
                <Paper
                    elevation={2}
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2,
                        maxHeight: 'calc(100vh - 32px)',
                        overflow: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
                            <PersonAddIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                            <Typography component="h1" variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                                Sign up
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                Create your AsroSync account
                            </Typography>
                        </Box>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    id="companyName"
                                    label="Company Name"
                                    value={companyName}
                                    onChange={(e) => setCompanyName(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    id="userEmail"
                                    label="Email"
                                    type="email"
                                    value={userEmail}
                                    onChange={(e) => setUserEmail(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Password"
                                    type="password"
                                    value={userPassword}
                                    onChange={(e) => setUserPassword(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Confirm Password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="First Name"
                                    value={userFirstName}
                                    onChange={(e) => setUserFirstName(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Last Name"
                                    value={userLastName}
                                    onChange={(e) => setUserLastName(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Address"
                                    value={userAddress}
                                    onChange={(e) => setUserAddress(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Country"
                                    value={country}
                                    onChange={(e) => setCountry(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Phone"
                                    value={userPhone}
                                    onChange={(e) => setUserPhone(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    size="small"
                                    required
                                    fullWidth
                                    label="Number of Users"
                                    type="number"
                                    value={numberOfUsers}
                                    onChange={(e) => setNumberOfUsers(e.target.value)}
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl size="small" fullWidth sx={fieldSx}>
                                    <InputLabel id="planName-label">Plan</InputLabel>
                                    <Select
                                        labelId="planName-label"
                                        value={planName}
                                        onChange={(e) => setPlanName(e.target.value)}
                                        label="Plan"
                                    >
                                        <MenuItem value="Trial">Trial</MenuItem>
                                        <MenuItem value="Basic">Basic</MenuItem>
                                        <MenuItem value="Standard">Standard</MenuItem>
                                        <MenuItem value="Premium">Premium</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl size="small" fullWidth sx={fieldSx}>
                                    <InputLabel id="subscription-label">Billing</InputLabel>
                                    <Select
                                        labelId="subscription-label"
                                        value={subscription}
                                        onChange={(e) => setSubscription(e.target.value)}
                                        label="Billing"
                                    >
                                        <MenuItem value="monthly">Monthly</MenuItem>
                                        <MenuItem value="yearly">Yearly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            size="small"
                                            checked={cdxUser}
                                            onChange={(e) => setCdxUser(e.target.checked)}
                                        />
                                    }
                                    label="CDX user"
                                />
                            </Grid>
                        </Grid>

                        {error && (
                            <Alert severity="error" sx={{ mt: 1.5, py: 0 }}>
                                {error}
                            </Alert>
                        )}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={isSubmitting}
                            sx={{ mt: 2, mb: 1, textTransform: 'none', fontWeight: 600 }}
                        >
                            {isSubmitting ? 'Creating account…' : 'Create account'}
                        </Button>

                        <Typography variant="body2" align="center" color="text.secondary">
                            Already have an account?{' '}
                            <Link component={RouterLink} to="/" underline="hover" fontWeight={600}>
                                Sign in
                            </Link>
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Register;
